import React, { useEffect, useId, useState } from "react"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleXmark, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import type { InteractionDescriptionProps } from "../InteractionTooltip/InteractionTooltip"

interface InputFieldProps extends InteractionDescriptionProps {
  type: "text" | "link" | "email" | "password" | "number" | "date" | "calender" | "time"
  value: string
  title?: string
  disable?: boolean
  icon?: IconProp
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  required?: boolean
  minLength?: number
  maxLength?: number
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"]
  pattern?: string
  iconColor?: string
  containerStyle?: React.CSSProperties
  inputStyle?: React.CSSProperties
  placeholderBackgroundColor?: string
  initialValue?: string
  animationDuration?: string
  animationTypes?: keyof typeof AnimationTypes
  onInvalidMessage?: string
  id?: string
  name?: string
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"]
  errorMessage?: string
  min?: number | string
  max?: number | string
  step?: number | string
}

const InputField: React.FC<InputFieldProps> = ({
  type,
  disable,
  title = "",
  icon,
  value,
  initialValue,
  onInput,
  onChange,
  placeholder,
  required,
  minLength,
  maxLength,
  inputMode,
  pattern,
  iconColor,
  containerStyle,
  inputStyle,
  placeholderBackgroundColor,
  id,
  animationDuration,
  animationTypes = AnimationTypes.fadeInTraslate,
  onInvalidMessage,
  name,
  autoComplete,
  errorMessage,
  min,
  max,
  step,
  descriptionKey,
}) => {
  const { t } = useTranslation("general")

  const [showPassword, setShowPassword] = useState(false)

  const [isFocused, setIsFocused] = useState(false)

  const generatedInputId = useId()

  const inputId = id || generatedInputId

  const errorId = `${inputId}-error`

  const hasError = Boolean(errorMessage)

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === "number") e.currentTarget.blur()
  }

  const handleClear = () => {
    onChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>)
  }

  const showCounter = Boolean(maxLength && value && type !== "date")

  const inputPaddingRightClass = showCounter ? (type === "password" ? "pr-28" : "pr-32") : "pr-10"

  const floatingPlaceholder = required ? `* ${placeholder}` : placeholder

  const shouldFloatPlaceholder =
    Boolean(placeholder) && (isFocused || Boolean(value) || type === "date" || type === "time" || type === "calender")

  const placeholderLeftClass = icon ? "left-10" : "left-5"

  const placeholderBackgroundClass = placeholderBackgroundColor ? "" : disable ? "bg-gray-100" : "bg-white"

  useEffect(() => {
    if (initialValue) {
      onChange({ target: { value: initialValue } } as React.ChangeEvent<HTMLInputElement>)
    }
  }, [initialValue])

  return (
    <Animate type={animationTypes} duration={animationDuration}>
      <div className="w-full space-y-1">
        {title && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-600 ml-2">
            {title}
          </label>
        )}

        <div
          data-interaction-description-key={descriptionKey}
          className={`relative flex items-center rounded-xl border bg-white ring-2 transition-all duration-200
            ${
              disable
                ? "cursor-not-allowed border-gray-200 bg-gray-100 ring-gray-200"
                : hasError
                  ? "border-red-400 ring-red-200 focus-within:border-red-500 focus-within:ring-red-300"
                  : "border-gray-300 ring-gray-200 focus-within:border-orange-400 focus-within:ring-orange-500"
            }
          `}
          style={containerStyle}
        >
          {icon ? (
            <div className="pl-3 pr-2 text-grey-800">
              <FontAwesomeIcon icon={icon} color={iconColor ?? undefined} />
            </div>
          ) : (
            <div className="pl-5"></div>
          )}

          <input
            data-interaction-description-key={descriptionKey}
            id={inputId}
            name={name}
            autoComplete={autoComplete}
            disabled={disable}
            type={type === "password" ? (showPassword ? "text" : "password") : type}
            value={value}
            required={required}
            minLength={minLength}
            maxLength={maxLength}
            min={min}
            max={max}
            step={step}
            inputMode={inputMode}
            pattern={pattern}
            aria-label={floatingPlaceholder}
            aria-description={t(descriptionKey)}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            placeholder=""
            onWheel={handleWheel}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onInput={(e) => {
              if (maxLength && e.currentTarget.value.length > maxLength) {
                e.currentTarget.value = e.currentTarget.value.slice(0, maxLength)
              }

              e.currentTarget.setCustomValidity("")
              if (onInput) onInput(e)
            }}
            onInvalid={(e) => e.currentTarget.setCustomValidity(onInvalidMessage ?? t("label_find_onInvalidMessage"))}
            onChange={onChange}
            className={`
              min-w-0 flex-1 py-2 bg-transparent
              outline-none text-gray-800
              disabled:text-gray-400
              ${inputPaddingRightClass}
            `}
            style={inputStyle}
          />

          {placeholder && (
            <label
              htmlFor={inputId}
              className={`
                pointer-events-none absolute z-10 max-w-[calc(100%-4.5rem)] truncate px-1 font-medium
                transition-all duration-200 ease-out rounded-2xl
                ${placeholderLeftClass}
                ${placeholderBackgroundClass}
                ${hasError ? "text-red-600" : "text-gray-400"}
                ${shouldFloatPlaceholder ? "top-0 -translate-y-1/2 text-[11px]" : "top-1/2 -translate-y-1/2 text-sm"}
              `}
              style={placeholderBackgroundColor ? { backgroundColor: placeholderBackgroundColor } : undefined}
            >
              {floatingPlaceholder}
            </label>
          )}

          {showCounter && (
            <span
              className={`absolute text-xs font-bold text-gray-500 ${type === "password" ? "right-12" : "right-10"}`}
            >
              {value.length}/{maxLength}
            </span>
          )}

          {type === "password" && value && !disable && (
            <button
              type="button"
              data-interaction-description-key="interactionDescriptions:shared.input.passwordVisibility"
              aria-label={t("interactionDescriptions:shared.input.passwordVisibility")}
              onClick={(e) => {
                e.preventDefault()
                setShowPassword((prev) => !prev)
              }}
              className="absolute right-3 text-gray-600 hover:text-gray-800 transition duration-200 hover:scale-125"
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          )}

          {value && !disable && type !== "password" && (
            <button
              type="button"
              data-interaction-description-key="interactionDescriptions:shared.input.clear"
              aria-label={t("interactionDescriptions:shared.input.clear")}
              onClick={(e) => {
                e.preventDefault()
                handleClear()
              }}
              className="absolute right-3 text-red-600 hover:scale-125 hover:text-red-800 transition duration-200"
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
          )}
        </div>

        {hasError && (
          <p id={errorId} className="ml-2 text-xs font-semibold leading-snug text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </Animate>
  )
}

export default InputField
